import socketIo from 'socket.io-client';
import { cookieDecryption, decryption, replaceShortNameAtIndex } from '../../../utils';
import useSocketStore from '../../store/messages/useSocketStore';
import useLoginStore from 'hooks/store/common/useLoginStore';
import useGroupChats from '../../store/conversations/useGroupChats';
import usePairChats from '../../store/conversations/usePairChats';
import useChatMessages from '../../store/conversations/useChatMessages';
import useGroupChatsHooks from '../conversations/useGroupChatsHooks';
import { ChatType, MessageReceiverType, StoreStatus } from '../../../static';
import { useParams } from 'react-router';
import { useEffect, useRef } from 'react';
import useChatMessagesHooks from '../conversations/useChatMessagesHooks';
import useToaster from 'hooks/store/toast/useToaster';
import useStatusChangeRuns from 'hooks/store/useStatusChangeRuns';
import { BULK_STATUS_TOAST_MS, buildStatusChangeToast } from 'utils/statusChangeToast';
import { MenteeBulkStatusChangeCompletedPayload } from 'types';

const requestHandler = (event: string, data: any) => {
  switch (event) {
    case 'join': {
      return data;
    }
    default: {
      return { data, event };
    }
  }
};

export default function useSocket() {
  const user = cookieDecryption('user');
  const socketData = useSocketStore();
  const { setState: setSessionExpire } = useLoginStore();
  const { setState } = socketData;
  const {
    setState: setGroupChatsState,
    updateChatAsRead,
    updateReactionLastActivityAndMoveChatToTop,
    restoreLastActivityAndResort,
    hasChat,
    addChatToTop,
    updateChatLastActivitySilentAndIncrementUnreadMessageCount,
  } = useGroupChats();
  const {
    hasMessage,
    addReactionToMessage,
    removeReactionFromMessage,
    addMessageToList,
    setState: setChatState,
    updateMessageStatus,
  } = useChatMessages();
  const { fetchGroupChatById } = useGroupChatsHooks();
  const { fetchChatMessageById } = useChatMessagesHooks();
  const { showToast } = useToaster();

  async function getSocket({ token, userData }: any) {
    const socket: any = socketIo(process.env.REACT_APP_SOCKET_POINT, {
      transports: ['websocket'],
    });

    setState({
      ...socketData.getState(),
      socket,
    });

    socket.on('connect', () => {
      const payload = {
        event: 'join',
        data: {
          user_id: userData?.userId,
          token: token,
        },
      };

      // Reset retry attempts for all failed messages
      setChatState({
        retryAttempts: {} as Record<string, number>,
      });
      sendEvent({ payload });
    });
  }

  // Completion of a status-change run, from either the partner settings toggle or a
  // bulk action on the participants tab. The event is advisory and is never the authority on
  // state: it is rendered as a toast and recorded for consumers to refresh from, and nothing
  // here holds a disabled flag that a lost event could leave stuck.
  function handleMenteeBulkStatusChangeCompleted(payload: MenteeBulkStatusChangeCompletedPayload) {
    if (!payload?.correlationId) {
      return;
    }

    useStatusChangeRuns.getState().recordCompletedRun(payload);

    // excludedAtSubmitCount keeps the completion toast from re-reporting mentees the submit
    // toast already explained. Without it a run of 50 that excluded 49 up front reported
    // "49 user statuses didn't update — try again" even though the worker failed nobody.
    const toast = buildStatusChangeToast(payload);
    showToast(toast.variant, toast.message, BULK_STATUS_TOAST_MS);
  }

  async function sendEvent({ payload, changeLoader }: any) {
    const { event, data } = payload;

    const { socket } = socketData.getState();
    if (!changeLoader) {
      setState({
        ...socketData.getState(),
        socketLoaders: {
          ...socketData?.getState()?.socketLoaders,
          [event]: true,
        },
      });
    } else {
      setState({
        ...socketData.getState(),
        socketLoaders: {
          ...socketData?.getState()?.socketLoaders,
          [changeLoader]: true,
        },
      });
    }

    if (!socket?.connected) {
      return false;
    }
    // IMP-1831: Messenger.ts never AES-decrypts any socket payload — 'join' reads
    // data.token directly and 'request' reads data.data — so encrypting here (the
    // old behavior whenever REACT_APP_DISABLE_ENCRYPTION wasn't 'true', which is
    // every configured environment) throws inside Encryption() (CryptoJS needs a
    // string, not this object), silently swallows to undefined, and round-trips as
    // a literal `null` over Socket.IO's JSON packing, crashing the backend's
    // null-unguarded handlers for both event types.
    socket.emit(event === 'join' ? 'join' : 'request', requestHandler(event, data));
    return true;
  }

  async function handleMessageSentEvent(eventPayload: {
    chatId: string;
    lastActivity: any;
    lastActivityAt: string;
    messageId: string;
  }) {
    const { chatId, lastActivity, lastActivityAt, messageId } = eventPayload;

    if (lastActivity?.type === 'message') {
      const { selectedChat } = useGroupChats.getState();
      if (selectedChat?._id === chatId) {
        const fetchedMessage = await fetchChatMessageById(chatId, messageId);
        if (fetchedMessage) {
          addMessageToList(fetchedMessage);
        }
      }
    }

    const { pagination } = useGroupChats.getState();
    const path = window.location.pathname;

    if (pagination.page !== 1 || !path.includes('team-inbox')) {
      setGroupChatsState({
        hasNewActivities: true,
      });
      return;
    }

    if (hasChat(chatId)) {
      if (lastActivity?.type === 'message') {
        updateChatLastActivitySilentAndIncrementUnreadMessageCount(chatId, lastActivity, lastActivityAt);
      }
    } else {
      await fetchGroupChatById(chatId).then(fetchedChat => {
        addChatToTop(fetchedChat);
      });
    }
  }

  async function handleMessageReactionSentEvent(eventPayload: {
    chatId: string;
    lastActivity: any;
    lastActivityAt: string;
    removedReaction: any;
  }) {
    const { chatId, lastActivity, lastActivityAt, removedReaction } = eventPayload;

    if (!chatId || !lastActivity || !lastActivity || !lastActivityAt) {
      return;
    }

    if (hasMessage(lastActivity?.reaction?.messageId || lastActivity?.message?.messageId)) {
      if (lastActivity?.type === 'reaction') {
        const { messageId, reaction, reactedBy } = lastActivity?.reaction;
        addReactionToMessage(messageId, reaction, reactedBy, lastActivityAt);
      } else if (removedReaction?.messageId) {
        if (hasMessage(removedReaction.messageId)) {
          removeReactionFromMessage(removedReaction.messageId, removedReaction.reaction, removedReaction.reactedBy);
        }
      }
    }

    if (hasChat(chatId)) {
      if (lastActivity?.type === 'reaction') {
        const { messageId, reaction, reactedBy, messageType, contentPreview } = lastActivity?.reaction;
        if (reactedBy === user.userId) return;

        updateReactionLastActivityAndMoveChatToTop(
          chatId,
          messageId,
          reaction,
          reactedBy,
          messageType,
          contentPreview,
          lastActivityAt
        );
      } else if (lastActivity?.type === 'message') {
        const newLastActivity = {
          type: 'message',
          message: {
            contentPreview: `Reaction removed`,
            messageType: 'Text',
          },
        };
        restoreLastActivityAndResort(chatId, newLastActivity);
      }
    } else if (!removedReaction) {
      await fetchGroupChatById(chatId).then(fetchedChat => {
        addChatToTop(fetchedChat);
      });
    }

    const { pagination } = useGroupChats.getState();
    const path = window.location.pathname;

    if (pagination.page !== 1 || !path.includes('team-inbox')) {
      if (!removedReaction) {
        setGroupChatsState({
          hasNewActivities: true,
        });
      }
      return;
    }
  }

  /**
   * Handles the socket event fired after the backend successfully marks
   * all messages in a chat as read. This event is emitted only to the user who
   * opened the chat and triggered the `markChatAsRead` event.
   *
   * @param response - Backend response containing:
   *  - success: Indicates whether the operation completed successfully.
   *  - chatId: ID of the chat that was marked as read.
   */
  async function handleChatMessagesMarkedAsReadEvent(response: { success: boolean; chatId: string }) {
    if (response && response.success && response.chatId) {
      updateChatAsRead(response.chatId);
    }
  }

  /**
   * Handles the message status update event emitted by the server.
   * This event is sent from the backend to the original sender of the message
   * when one or more of their sent messages change status.
   *
   * @param eventPayload - Event data sent from the server:
   *  - newStatus: The new message status ('Delivered' | 'Read').
   *  - updates: Array of message updates containing:
   *      - chatId: ID of the chat the message belongs to.
   *      - messageId: ID of the message whose status changed.
   */
  async function handleMessageStatusUpdate(eventPayload: {
    newStatus: 'Delivered' | 'Read';
    updates: { chatId: string; messageId: string }[];
  }) {
    if (eventPayload.updates && eventPayload.updates.length > 0) {
      updateMessageStatus(eventPayload);
    }
  }

  async function socketResponse({ response }: any) {
    setState({
      ...socketData.getState(),
      socketLoaders: {
        ...socketData?.getState()?.socketLoaders,
        [response?.event]: false,
        chatLoader: false,
      },
    });
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const queryString = url.search;
    const params = new URLSearchParams(queryString);
    const paramValue: any = params.get('query');
    const query = paramValue ? decryption(paramValue) : {};
    switch (response?.event) {
      case 'join': {
        // IMP-2141: the unread count comes from the REST endpoint App.tsx calls
        // at login, and from 'getCounts' pushes after that. Nothing is fetched
        // here. An earlier revision did fetch a snapshot on join to cover a
        // reconnect, which duplicated the request rather than adding anything.
        setState({
          ...socketData.getState(),
          socketConnected: true,
          socketConnectionCount: (socketData.getState().socketConnectionCount ?? 0) + 1,
        });

        break;
      }
      case 'partnerShortNameUpdate': {
        if (
          currentUrl?.includes('/messages/team-inbox') &&
          currentUrl?.match(/\/messages\/team-inbox\/([^?]+)/)?.[1]?.length === 24
        ) {
          const groupId = currentUrl?.match(/\/messages\/team-inbox\/([^?]+)/)?.[1];
          const connectionlist = socketData.getState()?.connectionList;
          const conversationlist = socketData.getState()?.messageList?.messageData ?? [];

          const index = connectionlist?.findIndex((item: any) => item?.group?._id === groupId);
          const uniqueChats = conversationlist.reduce((acc: any, current: any) => {
            const x = acc.find((item: any) => item._id === current._id);
            if (!x) {
              if (current.messageType === 'Welcome Message' && current.messageCategory === 'Welcome Prompt') {
                acc.push({
                  ...current,
                  message: current?.content?.replace(':attributes', response?.data?.shortName),
                  groupName: response?.data?.shortName,
                });
              } else {
                acc.push({
                  ...current,
                  groupName: response?.data?.shortName,
                });
              }
            }
            return acc;
          }, []);

          if (index !== -1) {
            const updatedGroup = {
              ...connectionlist[index].group,
              groupName: response?.data?.shortName,
            };
            connectionlist[index] = {
              ...connectionlist[index],
              group: updatedGroup,
            };
          }

          setState({
            connectionlist: connectionlist,
            messageList: { messageData: uniqueChats },
          });
        } else {
          const connectionlist = socketData.getState()?.connectionList;

          const index = connectionlist?.findIndex((item: any) => item?.group?._id === response?.data?.groupId);

          if (index !== -1) {
            const updatedGroup = {
              ...connectionlist[index].group,
              groupName: response?.data?.shortName,
            };
            connectionlist[index] = {
              ...connectionlist[index],
              group: updatedGroup,
            };
          }

          setState({
            connectionlist: connectionlist,
          });
        }

        break;
      }

      case 'partnerShortNameUpdate': {
        const currentPath = window.location.pathname;
        if (currentPath?.includes('/messages/team-inbox')) {
          const connectionList = socketData.getState()?.connectionList;
          const index = connectionList?.findIndex((item: any) => item?.group?._id === response?.data?.groupId);
          const updatedList = replaceShortNameAtIndex(connectionList, response?.data?.shortName, index);

          setState({
            connectionList: updatedList,
          });
        }
        if (
          currentPath?.includes('/messages/team-inbox') &&
          currentPath?.split('/messages/team-inbox')?.[1]?.length === 24
        ) {
          const groupId = currentPath?.split('/messages/team-inbox')?.[1];
          const messageList = socketData.getState()?.messageList?.messageData;

          const uniqueChats = messageList?.reduce((acc: any, current: any) => {
            const x = acc.find((item: any) => item._id === current._id);
            if (!x) {
              if (current?.messageCategory === 'Welcome Prompt') {
                acc?.push({
                  ...current,
                  message: current?.content?.replace(':attributes', response?.data?.shortName),
                });
              } else {
                acc.push(current);
              }
            }
            return acc;
          }, []);
          setState({
            messageList: { messageData: uniqueChats },
          });
        }
        break;
      }

      case 'getCounts': {
        setState({
          getNotificationCount: response?.data?.notificationCount,
        });
        break;
      }

      case 'readMsg': {
        let lastData = socketData?.getState();
        let nextResponse = { ...lastData };
        const array = nextResponse?.messageList?.messageData ?? [];
        let nextMessageListArray = [...array].map((message: any) => ({
          ...message,
          read: true,
        }));
        nextResponse = { ...nextResponse, messageData: nextMessageListArray };
        let newConnectionList = lastData?.connectionList?.map((e: any) =>
          e?.chatId === response.data.chatId ? { ...e, lastMessage: { ...e?.lastMessage, read: true } } : { ...e }
        );

        setState({
          messageList: nextResponse,
          connectionList: newConnectionList,
        });
        return;
      }

      case 'readNotification': {
        let nextNotificationResponse = socketData.getState()?.notificationResponse;
        const nextNotificationArray = nextNotificationResponse?.data?.map((notification: any) => ({
          ...notification,
          read: true,
        }));
        nextNotificationResponse = {
          ...nextNotificationResponse,
          data: nextNotificationArray,
        };

        setState({ notificationResponse: nextNotificationResponse });

        break;
      }

      case 'error': {
        setSessionExpire({ isSessionExpired: true });
        break;
      }
    }
  }

  return {
    getSocket,
    sendEvent,
    socketResponse,
    handleChatMessagesMarkedAsReadEvent,
    handleMessageReactionSentEvent,
    handleMessageSentEvent,
    handleMessageStatusUpdate,
    handleMenteeBulkStatusChangeCompleted,
  };
}
